import React, { useState, useEffect } from 'react';
import { X, Save, Upload, DollarSign, Package, Tag, Trash2, Plus, Users } from 'lucide-react';
import { useMutation } from '@apollo/client';
import { UPDATE_PRODUCT } from '../../apollo/mutations';
import Modal from '../common/Modal';


const EditProductModal = ({ product, onClose, onSuccess, deleteProduct }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    basePrice: '',
    available: true,
  });
  
  // ADD THIS LINE HERE - right after formData state
  const [priceTiers, setPriceTiers] = useState([]);
  
  
  const [updateProduct, { loading }] = useMutation(UPDATE_PRODUCT);

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name || '',
        description: product.description || '',
        category: product.category || '',
        basePrice: product.basePrice || '',
        available: product.available !== false,
      });
      // ADD THIS LINE HERE - inside the if statement, after setFormData
      setPriceTiers(product.priceTiers || []);
    }
  }, [product]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const { data } = await updateProduct({
        variables: {
          id: product.id,
          input: {
            name: formData.name,
            description: formData.description,
            category: formData.category,
            basePrice: parseFloat(formData.basePrice),
            available: formData.available,
            priceTiers: priceTiers
              .filter(tier => tier.groupSize && tier.price)
              .map(tier => ({
                groupSize: parseInt(tier.groupSize),
                price: parseFloat(tier.price)
              }))
          }
        }
      });
      
      if (onSuccess) {
        onSuccess(data.updateProduct);
      }
      onClose();
    } catch (error) {
      console.error('Update error:', error);
      alert('Failed to update product. Please try again.');
    }
  };

  if (!product) return null;

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Edit Product"
      size="large"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Product Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Product Name *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Describe your product..."
          />
        </div>

        {/* Category and Price Row */}
        <div className="grid grid-cols-2 gap-4">
          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category *
            </label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({...formData, category: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="">Select Category</option>
              <option value="Groceries">Groceries</option>
              <option value="Household">Household</option>
              <option value="Electronics">Electronics</option>
              <option value="Clothing">Clothing</option>
              <option value="Health">Health & Beauty</option>
              <option value="Office">Office Supplies</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {/* Base Price */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Base Price ($) *
            </label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-2.5 text-gray-400" size={18} />
              <input
                type="number"
                step="0.01"
                value={formData.basePrice}
                onChange={(e) => setFormData({...formData, basePrice: e.target.value})}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
          </div>
        </div>  

        <div className="border-t pt-4">
          <div className="flex justify-between items-center mb-3">
            <label className="text-sm font-medium text-gray-700">
              Bulk Pricing Tiers
            </label>
            <button
              type="button"
              onClick={() => setPriceTiers([...priceTiers, { groupSize: '', price: '' }])}
              className="text-blue-600 hover:text-blue-700 text-sm flex items-center"
            >
              <Plus size={16} className="mr-1" />
              Add Tier
            </button>
          </div>
  
          {priceTiers.length > 0 ? (
            <div className="space-y-2">
              {priceTiers.map((tier, index) => (
                <div key={index} className="flex gap-2 items-center">
                  <div className="flex-1">
                    <input
                      type="number"
                      placeholder="Min quantity"
                      value={tier.groupSize}
                      onChange={(e) => {
                        const newTiers = [...priceTiers];
                        newTiers[index].groupSize = e.target.value;
                        setPriceTiers(newTiers);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div className="flex-1">
                    <input
                      type="number"
                      step="0.01"
                      placeholder="Price at this qty"
                      value={tier.price}
                      onChange={(e) => {
                        const newTiers = [...priceTiers];
                        newTiers[index].price = e.target.value;
                        setPriceTiers(newTiers);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setPriceTiers(priceTiers.filter((_, i) => i !== index));
                    }}
                    className="text-red-600 hover:text-red-700 p-2"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
              <p className="text-xs text-gray-500 mt-2">
                Example: 10 units at $15.99, 25 units at $13.99
              </p>
            </div>
          ) : (
            <p className="text-sm text-gray-500">No bulk pricing tiers set. Add tiers for group discounts.</p>
          )}
        </div>

        {/* Availability Toggle */}
        <div className="flex items-center justify-between">
          <div>
            <label className="text-sm font-medium text-gray-700">Product Availability</label>
            <p className="text-xs text-gray-500 mt-1">Toggle whether this product is available for purchase</p>
          </div>
          <button
            type="button"
            onClick={() => setFormData({...formData, available: !formData.available})}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              formData.available ? 'bg-green-600' : 'bg-gray-200'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                formData.available ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {/* Current Status Display */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Current Status:</span>
              <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                product.approvalStatus === 'approved' ? 'bg-green-100 text-green-800' :
                product.approvalStatus === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
              }`}>
                {product.approvalStatus || 'Pending'}
              </span>
            </div>
            <div>
              <span className="text-gray-600">Product ID:</span>
              <span className="ml-2 font-mono text-xs">{product.id}</span>
            </div>
          </div>
        </div>

        {/* Delete Product Section */}
        <div className="border-t pt-4">
          <button
            type="button"
            onClick={() => {
              if (window.confirm('Are you sure you want to delete this product? This action cannot be undone.')) {
                deleteProduct(product.id);
                onClose();
              }
            }}
            className="text-red-600 hover:text-red-700 text-sm flex items-center"
          >
            <Trash2 size={16} className="mr-2" />
            Delete Product
          </button>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Save size={18} className="mr-2" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default EditProductModal;
